import Provider from "../enum/Provider.enum";
import Role from "../enum/Role.enum";

export const DEFAULT_LANGUAGE = "ru";

export const LANGUAGES = ["ru", "en", "ar", "hy", "tr"] as const;

export type AppLanguage = (typeof LANGUAGES)[number];

type LanguageCode = AppLanguage;

type ResolveLanguageOptions = {
  provider?: Provider;
  roles?: string[];
  preferredLanguage?: string | null;
  fallbackLanguage?: string | null;
};

const AVAILABLE_LANGUAGES_BY_PROVIDER: Record<Provider, LanguageCode[]> = {
  [Provider.ARMENIA]: ["hy", "ru", "en"],
  [Provider.UAE]: ["en", "ar", "ru"],
  [Provider.BELARUS]: ["ru", "en"],
  [Provider.TFOS]: ["en", "ru"],
  [Provider.DEFAULT]: ["ru", "en", "hy", "tr"],
};

export const normalizeLanguage = (
  language?: string | null,
): LanguageCode | null => {
  const code = String(language || "").split("-")[0] as LanguageCode;

  return LANGUAGES.includes(code) ? code : null;
};

export const getAvailableLanguages = (
  provider?: Provider,
  roles: string[] = [],
): LanguageCode[] => {
  const isAdmin = roles.includes(Role.AGENT_ADMIN);

  if (provider === Provider.UAE && !isAdmin) {
    return ["en", "ar"];
  }

  if (provider === Provider.TFOS && !isAdmin) {
    return ["en"];
  }

  return (
    AVAILABLE_LANGUAGES_BY_PROVIDER[provider ?? Provider.DEFAULT] ??
    AVAILABLE_LANGUAGES_BY_PROVIDER[Provider.DEFAULT]
  );
};

export const getDefaultLanguage = (
  provider?: Provider,
  roles: string[] = [],
): LanguageCode => {
  const availableLanguages = getAvailableLanguages(provider, roles);

  if (
    provider === Provider.TFOS ||
    (provider === Provider.UAE && !roles.includes(Role.AGENT_ADMIN))
  ) {
    return "en";
  }

  return availableLanguages.includes(DEFAULT_LANGUAGE)
    ? DEFAULT_LANGUAGE
    : (availableLanguages[0] ?? DEFAULT_LANGUAGE);
};

export const resolveLanguage = ({
  provider,
  roles = [],
  preferredLanguage,
  fallbackLanguage,
}: ResolveLanguageOptions): LanguageCode => {
  const availableLanguages = getAvailableLanguages(provider, roles);
  const normalizedPreferredLanguage = normalizeLanguage(preferredLanguage);

  if (
    normalizedPreferredLanguage &&
    availableLanguages.includes(normalizedPreferredLanguage)
  ) {
    return normalizedPreferredLanguage;
  }

  const normalizedFallbackLanguage = normalizeLanguage(fallbackLanguage);

  if (
    normalizedFallbackLanguage &&
    availableLanguages.includes(normalizedFallbackLanguage)
  ) {
    return normalizedFallbackLanguage;
  }

  return getDefaultLanguage(provider, roles);
};

export const RTL_LANGUAGES: LanguageCode[] = ["ar"];

export const isRtlLanguage = (language?: string | null): boolean => {
  const normalized = normalizeLanguage(language);
  return !!normalized && RTL_LANGUAGES.includes(normalized);
};
